import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Budget, Material, GeneratedBy } from '../interfaces';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { BudgetModel } from '../models/budget-model';
import { timer } from 'rxjs/observable/timer';
import { distinctUntilChanged, debounce, filter } from 'rxjs/operators';
import { Subscription } from 'rxjs/Subscription';
import { SubBudgetModel } from '../models/sub-budget';

@Component({
  selector: 'app-export-form',
  templateUrl: './export-form.component.html',
  styleUrls: ['./export-form.component.scss']
})
export class ExportFormComponent implements OnInit, OnDestroy {

  @Input() set budget(b: Budget) {
    this._budget = b;
    if (this.exportForm) {
      this.exportForm.patchValue({
        exchangeRate: this._budget.export ? this._budget.export.exchangeRate : undefined,
      }, {
          emitEvent: false
        });
    }
  }


  public exportForm: FormGroup;
  public exchangeRateHint;
  private justUpdatedFromFix = false;
  private _budget;

  private subscriptionContainer: Subscription = new Subscription();

  private _designVal: number;
  private _shippingVal: number;

  constructor(private dataService: DataService, private fb: FormBuilder) { }

  ngOnInit() {

    // Get export value for design and shipping
    const gdmS = this.dataService.getSBMaterial(this._budget.id, 'AUTO_BUDGET', 'design')
    .pipe(filter((mat) => mat != null))
      .subscribe((mat: Material) => {
        this._designVal = mat.costEach;
        this.exportForm.patchValue({
          ingCost: this._designVal
        },
          {
            emitEvent: false
          });
      });
    this.subscriptionContainer.add(gdmS);

    const gsmS = this.dataService.getSBMaterial(this._budget.id, 'AUTO_BUDGET', 'shipping')
    .pipe(filter((mat) => mat != null))
      .subscribe((mat: Material) => {
        this._shippingVal = mat.costEach;
        this.exportForm.patchValue({
          shipping: this._shippingVal
        },
          {
            emitEvent: false
          });

      });
    this.subscriptionContainer.add(gsmS);


    // Definition of the Margin Form
    // build the form model
    this.exportForm = this.fb.group({
      exchangeRate: [this._budget.export ? this._budget.export.exchangeRate : undefined, Validators.min(0)],
      ingCost: [this._designVal ? this._designVal : 0, Validators.min(0)],
      shipping: [this._shippingVal ? this._shippingVal : 0, Validators.min(0)],
    });

    if (!BudgetModel.isEditable(this._budget)) {
      this.exportForm.disable();
    }

    // Enable auto commit on change
    this.onChange();


  }

  public refreshRatio = () => {
    this.dataService.getExchangeRate('USD')
      .subscribe((r) => {

        this.exchangeRateHint = 'Ratio update from https://fixer.io';
        this.justUpdatedFromFix = true;
        this.exportForm.patchValue({
          exchangeRate: r
        });

      });
  }

  // Auto commit on value change
  private onChange(): void {

    // EXCHANGE RATIO
    this.exportForm.get('exchangeRate').valueChanges
      .do(() => {
        if (!this.justUpdatedFromFix) {
          this.exchangeRateHint = '';
        }
        this.justUpdatedFromFix = false;
      })
      .pipe(
        debounce(() => timer(500)),
        filter(() => this.exportForm.get('exchangeRate').valid),
        distinctUntilChanged()
      )
      .subscribe(val => {
        const exportData = this._budget.export ? this._budget.export : {};
        exportData.exchangeRate = this.exportForm.value.exchangeRate;
        this.dataService.patchBudget(this._budget.id, {
          export: exportData
        })
          .then((data) => {
            console.log('Budget patched');
          });
      });

    // DESIGN
    this.exportForm.get('ingCost').valueChanges
      .pipe(
        debounce(() => timer(500)),
        filter(() => this.exportForm.get('ingCost').valid),
        distinctUntilChanged()
      )
      .subscribe(val => {
        const exportDesign = this.exportForm.value.ingCost;
        const batch = this.dataService.getBatch();

        this.dataService.updateSubBudget(this._budget.id, 'AUTO_BUDGET',
        new SubBudgetModel('Overseas', GeneratedBy.Export, 'AUTO_BUDGET', true), batch);

        this.dataService.setSBMaterial(this._budget.id, 'AUTO_BUDGET', 'design', {
          description: 'Overseas Design',
          category: 'IUS',
          costEach: exportDesign,
          quantity: 1,
          unit: 'pieces'
        }, batch);

        return batch.commit()
          .then((data) => {
            console.log('Budget patched', data);
            return this.dataService.getBudgetCostSummary(this._budget.id).toPromise();
          })
          .then((data) => {
            console.log('Summary updated', data);
          })
          .catch((err) => {
            console.log('Error', err);
          });

      });

    // SHIPPING
    this.exportForm.get('shipping').valueChanges
      .pipe(
        debounce(() => timer(500)),
        filter(() => this.exportForm.get('shipping').valid),
        distinctUntilChanged()
      )
      .subscribe(val => {
        const exportShipping = this.exportForm.value.shipping;
        const batch = this.dataService.getBatch();

        this.dataService.updateSubBudget(this._budget.id, 'AUTO_BUDGET',
        new SubBudgetModel('Overseas', GeneratedBy.Export, 'AUTO_BUDGET', true), batch);

        this.dataService.setSBMaterial(this._budget.id, 'AUTO_BUDGET', 'shipping', {
          description: 'Overseas Shipping',
          category: 'SPE',
          costEach: exportShipping,
          quantity: 1,
          unit: 'pieces'
        }, batch);

        return batch.commit()
          .then((data) => {
            console.log('Budget patched', data);
            return this.dataService.getBudgetCostSummary(this._budget.id).toPromise();
          })
          .then((data) => {
            console.log('Summary updated', data);
          })
          .catch((err) => {
            console.log('Error', err);
          });

      });
  }

  submit() {

    // const costSummary = this._budget.costSummary ? this._budget.costSummary : { assembly: 0 };
    const exportDesign = this.exportForm.value.ingCost;
    const exportShipping = this.exportForm.value.shipping;
    const exportProduction = 0;

    const exportData = this._budget.export ? this._budget.export : {};
    exportData.exchangeRate = this.exportForm.value.exchangeRate;

    const batch = this.dataService.getBatch();

    this.dataService.updateSubBudget(this._budget.id, 'AUTO_BUDGET',
    new SubBudgetModel('Overseas', GeneratedBy.Export, 'AUTO_BUDGET', true), batch);

    this.dataService.setSBMaterial(this._budget.id, 'AUTO_BUDGET', 'design', {
      description: 'Overseas Design',
      category: 'IUS',
      costEach: exportDesign,
      quantity: 1,
      unit: 'pieces'
    }, batch);

    this.dataService.setSBMaterial(this._budget.id, 'AUTO_BUDGET', 'shipping', {
      description: 'Overseas Shipping',
      category: 'SPE',
      costEach: exportShipping,
      quantity: 1,
      unit: 'pieces'
    }, batch);

    return batch.commit()
      .then((data) => {
        console.log('Budget patched', data);
        return this.dataService.getBudgetCostSummary(this._budget.id).toPromise();
      })
      .then((data) => {
        console.log('Summary updated', data);
      })
      .catch((err) => {
        console.log('Error', err);
      });

  }

  ngOnDestroy() {
    this.subscriptionContainer.unsubscribe();
  }

}




// WEBPACK FOOTER //
// ./src/app/budget/components/export-form.component.ts